/**
 * Every offer that arrived in a different UOM than the RFQ asked for, OR was excluded from
 * ranking outright — for a bad UOM conversion or for a currency with no FX rate to the base
 * (`lib/compare.ts`'s `collectUomNotes`) — and what the workbench did with it.
 *
 * This is what makes the ranking auditable. Two rates side by side look comparable whether or
 * not they are priced per the same unit — or even the same currency — so a purchaser who
 * cannot see the conversions has to take "cheapest" on faith. Converted offers show the factor
 * and the restated rate; excluded ones name the supplier that is missing from the row and WHY,
 * using `note.reason` verbatim rather than assuming every exclusion is a UOM problem — a
 * currency-only exclusion has a perfectly good UOM match, and telling the purchaser to fix a
 * conversion that was never broken sends them chasing the wrong problem.
 *
 * Collapsed by default: it is reassurance on a normal enquiry and a work list on a bad one.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Card } from '../../components/ui'
import { Money } from '../../components/Money'
import { formatNumber } from '../../lib/format'
import type { UomNote } from '../../lib/compare'
import type { Currency } from '../../lib/types'

export function UomNotesPanel({ notes, baseCurrency }: { notes: UomNote[]; baseCurrency: Currency }) {
  const excluded = notes.filter((n) => n.handled === 'excluded')
  const converted = notes.length - excluded.length

  /*
    OPEN when something was excluded, collapsed when everything converted cleanly.

    This module's own header says the panel is "reassurance on a normal enquiry and a work list on
    a bad one" — but it opened collapsed either way, so the work list was invisible. An excluded
    offer means a supplier is MISSING from the row: the purchaser can allocate, price and send a
    quotation having never seen that someone quoted and was dropped for a unit mismatch.

    `useState`'s initialiser, not an effect: it is the starting position, and a purchaser who
    collapses it has answered the question and should not have it reopened under them.
  */
  const [open, setOpen] = useState(excluded.length > 0)

  if (notes.length === 0) return null

  return (
    <Card
      style={
        // An excluded offer means the comparison is incomplete — that reads as a warning,
        // while a set of clean conversions is just information.
        excluded.length > 0 ? { borderColor: 'var(--dangerline)', background: 'var(--dangerbg)' } : undefined
      }
    >
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexWrap: 'wrap' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
      >
        <b>
          {excluded.length > 0 ? '⚠' : '⇄'} Conversion issues on {notes.length} offer
          {notes.length === 1 ? '' : 's'}
        </b>
        {converted > 0 ? <Badge tone="info">{converted} converted &amp; ranked</Badge> : null}
        {excluded.length > 0 ? <Badge tone="danger">{excluded.length} excluded — not ranked</Badge> : null}
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{open ? 'hide ▲' : 'show ▼'}</span>
      </div>

      {open ? (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Supplier</th>
                <th>RFQ asked</th>
                <th>Supplier quoted</th>
                <th>Conversion</th>
                <th className="right">Comparable rate</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                // Keyed on (item_code, enquiry_row_no, supplier) — two enquiry lines can share
                // an item_code (WorkbenchTable's own rows have the same collision to avoid).
                <tr key={`${note.item_code}::${note.enquiry_row_no}-${note.supplier}`}>
                  <td>
                    <div>{note.item_name}</div>
                    <div className="mono" style={{ color: 'var(--muted)', fontSize: 11 }}>
                      {note.item_code}
                    </div>
                  </td>
                  <td>{note.supplier}</td>
                  <td>
                    <b>{note.rfq_uom}</b>
                  </td>
                  <td>
                    <Money value={note.quoted} />/<b>{note.sq_uom}</b>
                  </td>
                  <td>
                    {note.factor === null ? (
                      // The real reason this offer was dropped — a bad UOM conversion and a
                      // missing FX rate are different problems with different fixes, and this
                      // is the only place the purchaser can tell them apart.
                      <Badge tone="danger" title={note.reason}>
                        {note.reason}
                      </Badge>
                    ) : (
                      // Stated in whichever direction reads as a whole number.
                      <Badge tone="info">
                        {note.factor >= 1
                          ? `1 ${note.sq_uom} = ${formatNumber(note.factor, 2)} ${note.rfq_uom}`
                          : `1 ${note.rfq_uom} = ${formatNumber(1 / note.factor, 2)} ${note.sq_uom}`}
                      </Badge>
                    )}
                  </td>
                  <td className="right">
                    {note.unitInBase === null ? (
                      <span style={{ color: 'var(--muted)' }}>excluded from ranking</span>
                    ) : (
                      <>
                        <Money value={{ amount: note.unitInBase, currency: baseCurrency }} />
                        <span style={{ color: 'var(--muted)' }}>/{note.rfq_uom}</span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {excluded.length > 0 ? (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
              An excluded offer is left out of best-per-item and every strategy preview — ranking it on a rate
              per the wrong unit or an unconvertible currency would be wrong, not merely imprecise. Each row's
              own reason above says which: add the missing conversion to the item master, ask the supplier to
              requote in the RFQ's unit, or add the missing exchange rate in Settings.
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}
